import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MapPin,
  Utensils,
  Wine,
  Waves,
  Sparkles,
  Coffee,
  HelpCircle,
} from "lucide-react";

const locations = [
  {
    id: "como",
    name: "Como",
    country: "Italy",
    costMultiplier: 1.4,
    revenueMultiplier: 1.3,
  },
  {
    id: "bali",
    name: "Bali",
    country: "Indonesia",
    costMultiplier: 0.6,
    revenueMultiplier: 0.8,
  },
  {
    id: "barcelona",
    name: "Barcelona",
    country: "Spain",
    costMultiplier: 1.0,
    revenueMultiplier: 1.0,
  },
  {
    id: "rome",
    name: "Rome",
    country: "Italy",
    costMultiplier: 1.1,
    revenueMultiplier: 1.1,
  },
];

const amenities = [
  {
    id: "breakfast",
    name: "Breakfast",
    icon: Coffee,
    costAdd: 50000,
    revenueAdd: 80000,
  },
  {
    id: "restaurant",
    name: "Restaurant",
    icon: Utensils,
    costAdd: 200000,
    revenueAdd: 350000,
  },
  { id: "bar", name: "Bar", icon: Wine, costAdd: 80000, revenueAdd: 120000 },
  {
    id: "spa",
    name: "Spa",
    icon: Sparkles,
    costAdd: 300000,
    revenueAdd: 200000,
  },
  {
    id: "pool",
    name: "Pool",
    icon: Waves,
    costAdd: 150000,
    revenueAdd: 100000,
  },
];

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `€${(value / 1000).toFixed(0)}K`;
  return `€${value}`;
};

export const HotelConfigurator = () => {
  const [selectedLocation, setSelectedLocation] = useState("barcelona");
  const [rooms, setRooms] = useState(25);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([
    "breakfast",
    "bar",
  ]);

  const toggleAmenity = (id: string) => {
    setSelectedAmenities((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  const metrics = useMemo(() => {
    const location = locations.find((l) => l.id === selectedLocation)!;
    const baseCostPerRoom = 120000;
    const baseRevenuePerRoom = 45000;
    const ebitdaMargin = 0.32;

    const roomCost = rooms * baseCostPerRoom * location.costMultiplier;
    const amenityCost = selectedAmenities.reduce((sum, id) => {
      const amenity = amenities.find((a) => a.id === id);
      return sum + (amenity?.costAdd || 0);
    }, 0);
    const totalCost = roomCost + amenityCost;

    const roomRevenue = rooms * baseRevenuePerRoom * location.revenueMultiplier;
    const amenityRevenue = selectedAmenities.reduce((sum, id) => {
      const amenity = amenities.find((a) => a.id === id);
      return sum + (amenity?.revenueAdd || 0);
    }, 0);
    const annualRevenue = roomRevenue + amenityRevenue;

    const ebitda = annualRevenue * ebitdaMargin;
    const yieldOnCost = (ebitda / totalCost) * 100;

    return {
      costToBuild: totalCost,
      annualRevenue,
      ebitda,
      yieldOnCost,
    };
  }, [selectedLocation, rooms, selectedAmenities]);

  const location = locations.find((l) => l.id === selectedLocation)!;

  return (
    <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50">
      <div className="space-y-6">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-3 block">
            Location
          </label>
          <div className="flex flex-wrap gap-2">
            {locations.map((loc) => (
              <button
                key={loc.id}
                onClick={() => setSelectedLocation(loc.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedLocation === loc.id
                    ? "bg-[hsl(215,85%,45%)] text-white shadow-md hover:bg-[hsl(215,85%,45%)]/90"
                    : "bg-secondary/50 text-secondary-foreground hover:bg-secondary"
                }`}
              >
                <MapPin className="w-3 h-3 inline mr-1" />
                {loc.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-600 ">
              Rooms
            </label>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">25</div>
            </div>
          </div>

          <div className="relative pt-1">
            <input
              type="range"
              min={10}
              max={50}
              step={1}
              value={rooms}
              onChange={(e) => setRooms(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((rooms - 10) / (50 - 10)) * 100}%, #e5e7eb ${((rooms - 10) / (50 - 10)) * 100}%, #e5e7eb 100%)`,
              }}
            />

            <div className="flex justify-between text-xs text-gray-500">
              <span>10</span>
              <span>50</span>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-3 block">
            Amenities
          </label>
          <div className="flex flex-wrap gap-2">
            {amenities.map((amenity) => {
              const Icon = amenity.icon;
              const isSelected = selectedAmenities.includes(amenity.id);
              return (
                <button
                  key={amenity.id}
                  onClick={() => toggleAmenity(amenity.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    isSelected
                      ? "bg-[hsl(215,85%,45%,0.15)] text-[hsl(215,85%,45%)] border border-[hsl(215,85%,45%,0.3)] hover:bg-[hsl(215,85%,45%,0.2)]"
                      : "bg-secondary/30 text-muted-foreground hover:bg-secondary/50 border border-transparent"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {amenity.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="pt-4 border-t border-border/50">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative text-center p-5 rounded-lg border border-border bg-card overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary/20"></div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Cost to Build
              </p>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(metrics.costToBuild)}
              </p>
            </div>

            <div className="relative text-center p-5 rounded-lg border border-border bg-card overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500/20"></div>
              <div className="flex items-center justify-center gap-1.5 mb-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Annual EBITDA
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs p-3">
                      <p className="text-sm">
                        Earnings Before Interest, Taxes, Depreciation &
                        Amortization.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-500">
                {formatCurrency(metrics.ebitda)}
              </p>
            </div>

            <div className="relative text-center p-5 rounded-lg border border-border bg-card overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500/20"></div>
              <div className="flex items-center justify-center gap-1.5 mb-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Yield on Cost
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs p-3">
                      <p className="text-sm">
                        Annual EBITDA divided by total development cost.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-500">
                {metrics.yieldOnCost.toFixed(1)}%
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-border/30">
            <div className="w-2 h-2 rounded-full bg-primary/50"></div>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{rooms} rooms</span>{" "}
              in{" "}
              <span className="font-medium text-foreground">
                {location.name}
              </span>
              {", "}
              <span className="font-medium text-foreground">
                {location.country}
              </span>
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};
